import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Header from '../../Components/Header';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { useAuth } from '../../Utils/AuthProvider';
import { hp, wp } from '../../helpers/common';
import { fetchSpinWheels } from '../../services/spinWheelService';
import moment from 'moment';
import Avatar from '../../Components/Avatar';

import defaultUserImg from '../../../assets/images/defaultuser.png';

interface Wheel {
  id: string;
  title: string;
  description?: string;
  created_at: string;
  created_by?: string;
  community?: { name: string };
  created_by_user?: { id: string; name: string; image?: string };
}

const SpinWheelsList = () => {
  const navigation = useNavigation<any>();
  const { userData } = useAuth();

  const [wheels, setWheels] = useState<Wheel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadWheels();
  }, []);

  const loadWheels = async () => {
    try {
      setError(null);
      const result = await fetchSpinWheels(20);      
      if (result.success) {
        setWheels((result.data || []) as Wheel[]);
      } else {
        setError(result.msg || 'Could not load spin wheels');
        console.error('Error loading wheels:', result.msg);
      }
    } catch (error) {
      console.error('Error loading wheels:', error);
      setError('An error occurred while loading spin wheels');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadWheels();
  };

  const handleWheelPress = (wheelId: string) => {
    navigation.navigate('SpinWheel', { wheelId });
  };

  const renderWheelItem = ({ item }: { item: Wheel }) => (
    <TouchableOpacity
      style={styles.wheelCard}
      onPress={() => handleWheelPress(item.id)}
    >
      <View style={styles.wheelHeader}>
        <View style={styles.wheelInfo}>
          <Text style={styles.wheelTitle}>{item.title}</Text>
          {item.description && (
            <Text style={styles.wheelDescription} numberOfLines={2}>
              {item.description}
            </Text>
          )}
        </View>
      </View>

      {item.community && (
        <View style={styles.communityTag}>
          <Text style={styles.communityTagText}>{item.community.name}</Text>
        </View>
      )}

      <View style={styles.wheelFooter}>
        <View style={styles.userInfo}>
          <Avatar
            uri={item.created_by_user?.image || defaultUserImg}
            size={30}
          />
          <Text style={styles.userName}>{item.created_by_user?.name || 'Unknown'}</Text>
        </View>
        <Text style={styles.timeText}>
          {moment(item.created_at).fromNow()}
        </Text>
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <ScreenWrapper>
        <Header title="Spin Wheels" showBackButton />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading spin wheels...</Text>
        </View>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <Header title="Spin Wheels" showBackButton />
      
      {error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorIcon}>⚠️</Text>
          <Text style={styles.errorTitle}>Database Setup Required</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Text style={styles.errorHint}>
            To use the Spin Wheel feature, you need to run the database schema first.
          </Text>
          <Text style={styles.setupStepsTitle}>Setup Steps:</Text>
          <Text style={styles.setupStep}>1. Open Supabase Console</Text>
          <Text style={styles.setupStep}>2. Go to SQL Editor</Text>
          <Text style={styles.setupStep}>3. Copy contents of db-spin-wheel.sql</Text>
          <Text style={styles.setupStep}>4. Paste and Run in SQL Editor</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadWheels}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={wheels}
          renderItem={renderWheelItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContainer}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyIcon}>🎰</Text>
              <Text style={styles.emptyText}>No spin wheels available</Text>
              <Text style={styles.emptyHint}>
                Create a spin wheel from the backend to get started.
              </Text>
            </View>
          }
        />
      )}
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContainer: {
    padding: wp(4),
  },
  wheelCard: {
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  wheelHeader: {
    marginBottom: 10,
  },
  wheelInfo: {
    marginBottom: 5,
  },
  wheelTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 5,
  },
  wheelDescription: {
    fontSize: 14,
    color: theme.colors.textLight,
  },
  communityTag: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors.primary + '20',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 10,
  },
  communityTagText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  wheelFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userName: {
    fontSize: 14,
    color: theme.colors.text,
    marginLeft: 8,
    fontWeight: '500',
  },
  timeText: {
    fontSize: 12,
    color: theme.colors.textLight,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: theme.colors.textLight,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: hp(10),
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 10,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 5,
  },
  emptyHint: {
    fontSize: 14,
    color: theme.colors.textLight,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorIcon: {
    fontSize: 64,
    marginBottom: 10,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 10,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: 'red',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 15,
  },
  errorHint: {
    fontSize: 14,
    color: theme.colors.textLight,
    textAlign: 'center',
    marginBottom: 15,
  },
  setupStepsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginTop: 15,
    marginBottom: 10,
  },
  setupStep: {
    fontSize: 13,
    color: theme.colors.textLight,
    textAlign: 'left',
    marginBottom: 5,
    paddingLeft: 10,
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 20,
  },
  retryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default SpinWheelsList;

